/**
 * SystemModePanel - System Mode menu panel for module/panel management
 *
 * Wraps ModuleControls and provides Field Segments management UI.
 */

import React from 'react';
import { Grid3x3, ChevronDown, ChevronRight } from 'lucide-react';
import { SpacingSlider } from './SpacingSlider';
import { AzimuthPresetButtons } from './AzimuthPresetButtons';
import { SegmentListItem } from './SegmentListItem';

// Import ModuleControls from aurora folder
import { ModuleControls } from '../../aurora/ModuleControls';

interface ModuleSegment {
  id: number;
  bounds: [number, number][];
  modules: any[];
  rotation?: number;
  azimuth?: number;
  config?: {
    moduleSpacing: number;
    rowSpacing: number;
    targetCount: number | null;
  };
}

interface SystemModePanelProps {
  // Collapse states
  moduleCardCollapsed: boolean;
  setModuleCardCollapsed: (v: boolean) => void;
  fieldSegmentsCollapsed: boolean;
  setFieldSegmentsCollapsed: (v: boolean) => void;

  // ModuleControls props (pass-through)
  moduleControlsProps: React.ComponentProps<typeof ModuleControls>;

  // Segment management
  moduleSegments: Record<number, ModuleSegment[]>;
  setModuleSegments: (segments: Record<number, ModuleSegment[]>) => void;
  selectedSegment: { polygonId: number; index: number } | null;
  setSelectedSegment: (segment: { polygonId: number; index: number } | null) => void;
  modules: Record<number, any[]>;
  setModules: (modules: Record<number, any[]>) => void;
  modulePowerWatts: number;
  moduleSpacing: number;
  rowSpacingMultiplier: number;
  FREE_FIELD_POLYGON_ID: number;
  refillSegment: (polygonId: number, segmentIndex: number, bounds: [number, number][]) => void;

  // Drawing controls
  isDrawingSegment?: boolean;
  onStartDrawingSegment?: () => void;
  onCancelDrawingSegment?: () => void;
  onCopySegment?: () => void;

  // Free-field mode
  isFreeFieldMode?: boolean;
  onFreeFieldModeChange?: (enabled: boolean) => void;
}

export function SystemModePanel({
  moduleCardCollapsed,
  setModuleCardCollapsed,
  fieldSegmentsCollapsed,
  setFieldSegmentsCollapsed,
  moduleControlsProps,
  moduleSegments,
  setModuleSegments,
  selectedSegment,
  setSelectedSegment,
  modules,
  setModules,
  modulePowerWatts,
  moduleSpacing,
  rowSpacingMultiplier,
  FREE_FIELD_POLYGON_ID,
  refillSegment,
  isDrawingSegment = false,
  onStartDrawingSegment,
  onCancelDrawingSegment,
  onCopySegment,
  isFreeFieldMode = false,
  onFreeFieldModeChange,
}: SystemModePanelProps) {
  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setModuleCardCollapsed(!moduleCardCollapsed)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <Grid3x3 className="h-5 w-5 text-gray-700" />
          <span className="text-sm font-semibold text-gray-900">Panels</span>
        </div>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${!moduleCardCollapsed ? 'rotate-180' : ''}`} />
      </button>

      {/* Module Controls - Expanded inline */}
      {!moduleCardCollapsed && (
        <div className="bg-gray-50 border-t border-gray-200 module-controls-inline">
          <style>{`
            .module-controls-inline .bg-white { background: transparent !important; }
            .module-controls-inline .border-2 { border: 0 !important; }
            .module-controls-inline .shadow-2xl { box-shadow: none !important; }
            .module-controls-inline .rounded-xl { border-radius: 0 !important; }
            .module-controls-inline > div > div:first-child { display: none !important; }
          `}</style>
          <ModuleControls {...moduleControlsProps} />
        </div>
      )}

      {/* Field Segments Section */}
      <div className="p-3 border-t border-gray-200">
        <div
          className="flex items-center justify-between mb-2 cursor-pointer hover:bg-gray-50 -mx-1 px-1 py-1 rounded"
          onClick={() => setFieldSegmentsCollapsed(!fieldSegmentsCollapsed)}
        >
          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <span>📐</span>
            Field Segments ({Object.values(moduleSegments).flat().length})
          </h4>
          {fieldSegmentsCollapsed ? (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </div>

        {/* Segment List */}
        {!fieldSegmentsCollapsed && (
          <>
            {/* Free-Field Mode Toggle */}
            {onFreeFieldModeChange && (
              <div className="p-2 bg-green-50 rounded-lg mb-3 border border-green-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-700">🌱 Free-Field Mode</span>
                    <span className="text-[10px] text-gray-500">(no roof needed)</span>
                  </div>
                  <button
                    onClick={() => onFreeFieldModeChange(!isFreeFieldMode)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      isFreeFieldMode ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                    title={isFreeFieldMode ? 'Free-field mode ON - draw anywhere' : 'Free-field mode OFF - select a roof first'}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      isFreeFieldMode ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
              </div>
            )}

            {/* Draw Segment Button with Feedback */}
            {onStartDrawingSegment && (
              <div className="mb-3">
                {isDrawingSegment ? (
                  <div className="space-y-2">
                    <div className="bg-cyan-100 border border-cyan-400 rounded-lg p-2 text-xs">
                      <p className="font-semibold text-cyan-800 mb-1">Drawing Field Segment...</p>
                      <p className="text-cyan-700">• Click to add vertices</p>
                      <p className="text-cyan-700">• Double-click or Enter to finish</p>
                      <p className="text-cyan-700">• ESC to cancel</p>
                    </div>
                    {onCancelDrawingSegment && (
                      <button
                        onClick={onCancelDrawingSegment}
                        className="w-full py-2 px-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-xs font-medium transition-colors"
                      >
                        Cancel Drawing
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={onStartDrawingSegment}
                    className="w-full py-2 px-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    <span>+</span>
                    <span>Draw Field Segment</span>
                  </button>
                )}
              </div>
            )}

            {Object.values(moduleSegments).flat().length > 0 ? (
              <div className="space-y-1 max-h-32 overflow-y-auto mb-2">
                {Object.entries(moduleSegments).map(([polygonId, segments]) =>
                  segments.map((segment, index) => {
                    const isSelected = selectedSegment?.polygonId === Number(polygonId) && selectedSegment?.index === index;
                    const segmentCapacity = (segment.modules.length * modulePowerWatts / 1000).toFixed(2);
                    const segmentAzimuth = segment.azimuth ?? segment.rotation ?? 180;
                    const isFreeField = Number(polygonId) === FREE_FIELD_POLYGON_ID;

                    return (
                      <SegmentListItem
                        key={`seg-list-${polygonId}-${segment.id}`}
                        segmentId={segment.id}
                        polygonId={Number(polygonId)}
                        moduleCount={segment.modules.length}
                        capacityKw={segmentCapacity}
                        azimuth={segmentAzimuth}
                        isSelected={isSelected}
                        isFreeField={isFreeField}
                        onSelect={() => setSelectedSegment({ polygonId: Number(polygonId), index })}
                        onDelete={() => {
                          const updatedSegments = moduleSegments[Number(polygonId)].filter((_, i) => i !== index);
                          setModuleSegments({
                            ...moduleSegments,
                            [polygonId]: updatedSegments
                          });
                          const remainingModules = (modules[Number(polygonId)] || []).filter(
                            m => m.segmentId !== segment.id
                          );
                          setModules({
                            ...modules,
                            [polygonId]: remainingModules
                          });
                          if (isSelected) setSelectedSegment(null);
                        }}
                      />
                    );
                  })
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-500 mb-2 italic">No segments yet. Use "Draw Segment" above.</p>
            )}

            {/* Selected Segment Controls */}
            {selectedSegment && moduleSegments[selectedSegment.polygonId]?.[selectedSegment.index] && (() => {
              const seg = moduleSegments[selectedSegment.polygonId][selectedSegment.index];
              return (
                <div className="mt-2 p-2 bg-green-50 border border-green-300 rounded-lg space-y-2">
                  <div className="text-xs font-semibold text-green-800 mb-1">
                    Editing Segment #{seg.id}
                  </div>

                  {/* Module Spacing */}
                  <SpacingSlider
                    label="Module Spacing"
                    value={seg.config?.moduleSpacing || moduleSpacing}
                    min={0}
                    max={0.1}
                    step={0.01}
                    formatValue={(v) => `${v.toFixed(2)}m`}
                    onChange={(newSpacing) => {
                      const updatedSegments = [...moduleSegments[selectedSegment.polygonId]];
                      updatedSegments[selectedSegment.index] = {
                        ...seg,
                        config: { ...seg.config, moduleSpacing: newSpacing }
                      };
                      setModuleSegments({
                        ...moduleSegments,
                        [selectedSegment.polygonId]: updatedSegments
                      });
                    }}
                    onChangeEnd={() => {
                      refillSegment(selectedSegment.polygonId, selectedSegment.index, seg.bounds);
                    }}
                  />

                  {/* Row Spacing */}
                  <SpacingSlider
                    label="Row Spacing"
                    value={seg.config?.rowSpacing || rowSpacingMultiplier}
                    min={1.0}
                    max={2.5}
                    step={0.1}
                    formatValue={(v) => `${v.toFixed(1)}x`}
                    onChange={(newSpacing) => {
                      const updatedSegments = [...moduleSegments[selectedSegment.polygonId]];
                      updatedSegments[selectedSegment.index] = {
                        ...seg,
                        config: { ...seg.config, rowSpacing: newSpacing }
                      };
                      setModuleSegments({
                        ...moduleSegments,
                        [selectedSegment.polygonId]: updatedSegments
                      });
                    }}
                    onChangeEnd={() => {
                      refillSegment(selectedSegment.polygonId, selectedSegment.index, seg.bounds);
                    }}
                  />

                  {/* Azimuth / Edge Selection */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Azimuth: {seg.azimuth ?? seg.rotation ?? 180}°
                    </label>
                    <AzimuthPresetButtons
                      currentAzimuth={seg.azimuth ?? seg.rotation ?? 180}
                      onSelectAzimuth={(az) => {
                        const updatedSegments = [...moduleSegments[selectedSegment.polygonId]];
                        updatedSegments[selectedSegment.index] = {
                          ...seg,
                          rotation: az,
                          azimuth: az
                        };
                        setModuleSegments({
                          ...moduleSegments,
                          [selectedSegment.polygonId]: updatedSegments
                        });
                        setTimeout(() => {
                          refillSegment(selectedSegment.polygonId, selectedSegment.index, seg.bounds);
                        }, 50);
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-1 italic">
                      Or click segment edge on map to snap
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    {/* Copy Segment */}
                    {onCopySegment && (
                      <button
                        onClick={onCopySegment}
                        className="flex-1 py-1 px-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-xs font-medium transition-colors"
                      >
                        📋 Copy
                      </button>
                    )}
                    {/* Deselect button */}
                    <button
                      onClick={() => setSelectedSegment(null)}
                      className="flex-1 py-1 px-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-xs font-medium transition-colors"
                    >
                      Deselect
                    </button>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}

export default SystemModePanel;
